import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { comma, uncomma, useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import { ImageUpload } from "@/components/Common/ImageUpload";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import {
    ContractInfo,
    AccidentInfo,
    VehicleInfo,
    DriverInfo,
    AdditionalDriverInfo,
    OtherInfo,
    OtherEtcInfo,
    EtcInfo,
    InsuranceDeliveredInfo,
    InsuranceInfo,
    InsuranceTable,
    ReturnInfo,
    BusinessInfo,
    CapitalInfo,
    BillingInfo,
    TotalBillingInfo,
    TermsTable,
    TermsFile,
    AttachedFile,
} from "./components";
import {
    AgentPartnersPopup,
    SalesPartnersPopup,
    OrderListPopup,
    VehicleListPopup,
    TermListPopup,
    VehicleReturnPopup,
    BillingSettingPopup,
    InsuranceConfigPopup,
} from "./popups";

import { useLazyGetCarByIdQuery, useLazySearchCarByNumberQuery } from "@/redux/carApi";
import { useGetOrderDetailMutation } from "@/redux/orderApi";
import { usePostChargeMutation, usePostPreChargeMutation } from "@/redux/chargeApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import {
    useDeleteContractGroupMutation,
    useGetContractGroupByIdQuery,
    usePostContractGroupMutation,
    usePutContractGroupMutation,
    useReturnContractMutation,
} from "@/redux/contractApi";
import { useGetTermListQuery } from "@/redux/termApi";
import { useGetInsuranceConfigDetailQuery, useGetInsuranceConfigListQuery } from "@/redux/rentCorpApi";

// 초기 계약서 객체
const initialContract = {
    rentCorpId: null,
    carId: null,
    contractCar: {
        carNumber: "",
        cho: "",
        manufacturerName: "",
        modelName: "",
        modelDetailName: "",
        className: "",
        trimName: "",
        bmNo: "",
        boiNo: "",
        boNo: "",
        bsNo: "",
        bdNo: "",
        firstDate: "",
        color: "",
        fuel: "",
        motorType: "",
        displacement: 0,
        mileage: 0,
        gasCurrentAmount: 0,
    },
    insuranceItems: [
        {
            insuranceItemType: "DAEIN_PAY_1", // 대인배상1
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEIN_PAY_2", // 대인배상2
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEMUL_PAY", // 대물배상
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "NO_INS_INJURY", // 무보험차상해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_DEAD", // 자동차상해(사망)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_INJURY", // 자동차상해(부상)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_BLOCK", // 자동차상해(장해)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "SELF_CAR_DAMAGE", // 자기차량손해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
            selfCarDamageRangeType: null, // "PRICE" or "RATE" 선택값
            selfCarDamageRateType: null,
        },
        {
            insuranceItemType: "EMERGENCY_SERVICE", // 긴급출동
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
    ],
    contractDrivers: [
        {
            contractDriverType: "MAIN",
            name: "",
            idNumber: "",
            phone: "",
            address: "",
            addressDetail: "",
            driverLicenseType: "",
            driverLicenseNumber: "",
            serialNumber: "",
            issueDate: "",
        },
    ],
    additionalDriverStatus: false,
};

export const ContractEditBasicInfo = () => {
    const { contractGroupId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const passedCarId = location.state?.carId;
    const passedOrderId = location.state?.orderId;

    const { formData, handleChange, setFormData } = useFormInput({
        // 공통
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        servicePopupStatus: false,
        addressPopupStatus: false,
        addressPopupTarget: null, // "MAIN" | "ADDITIONAL" | "ALLOCATION_RELEASE" | "ALLOCATION_RETURN" | "BUSINESS"
        id: null, // contractGroupId
        businessStatus: false,
        contractType: "",
        memoForClient: "",
        memoForContract: "",
        memoForInsCorp: "",
        contractUploads: [],
        contractTerms: [],
        driverLicenseFileName: "",
        driverLicense2FileName: "",
        bizLicenseFileName: "",
        etcFileName: "",
        addFileName: "",
        addTermFileName: "",
        contracts: [{ ...initialContract }],
        faxFileUploadId: undefined,
        faxFileUrl: undefined,
    });
    const { mode, toastMsg, confirmStatus, addressPopupStatus, isLoading } = formData;

    const [postContractGroup, { data: postData, isLoading: isPostLoading, error: isPostError }] = usePostContractGroupMutation();
    const [putContractGroup, { data: putData, isLoading: isPutLoading, error: isPutError }] = usePutContractGroupMutation();
    const [deleteContractGroup, { isLoading: isDeleteLoading }] = useDeleteContractGroupMutation();
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();
    const {
        data: contractGroupData,
        isLoading: isContractGroupLoading,
        isFetching: isContractGroupFetching,
        error: contractGroupError,
    } = useGetContractGroupByIdQuery(contractGroupId, {
        skip: !contractGroupId,
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    // 1) 모드 설정 (UPDATE / CREATE)
    useEffect(() => {
        if (contractGroupId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: contractGroupId,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                mode: "CREATE",
            }));
        }
    }, [contractGroupId, setFormData]);

    // 2) UPDATE 모드일 때: 계약 상세조회 (contractDetailData) 매핑
    useEffect(() => {
        if (contractGroupData && contractGroupData.data) {
            const response = contractGroupData.data;
            // contractBusinessInfo가 존재하면 businessStatus를 true로 설정
            const businessStatus = response.contractBusinessInfo ? true : false;

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                ...response,
                // contractDrivers 변환 결과 반영
                businessStatus: businessStatus,
                faxFileUploadId: response.charge?.faxFileUploadId || undefined,
                faxFileUrl: response.charge?.faxFileUrl || undefined,
                // 필요시 contractCar 등 다른 필드도 추가 매핑
            }));
        }
    }, [contractGroupId, contractGroupData, setFormData]);

    // buildRequestBody – 그룹 레벨 필드와 contracts 배열 포함
    const buildRequestBody = () => {
        let reqBody = { ...formData };

        // UI용 상태 값들 제거
        delete reqBody.mode;
        delete reqBody.toastMsg;
        delete reqBody.confirmStatus;
        delete reqBody.servicePopupStatus;
        delete reqBody.id;
        delete reqBody.additionalDriverStatus;
        delete reqBody.businessStatus;
        delete reqBody.driverLicenseFileName;
        delete reqBody.driverLicense2FileName;
        delete reqBody.bizLicenseFileName;
        delete reqBody.etcFileName;
        delete reqBody.addFileName;
        delete reqBody.addTermFileName;
        delete reqBody.vehicleReturnPopupStatus;
        delete reqBody.billingSettingPopupStatus;
        delete reqBody.addressPopupStatus;
        delete reqBody.addressPopupTarget;
        delete reqBody.agentPartnersPopupStatus;
        delete reqBody.salesPartnersPopupStatus;
        delete reqBody.vehicleListPopupStatus;

        // accidentCar.displacement가 문자열로 콤마 포함 시 uncomma 처리
        if (reqBody.accidentCar && typeof reqBody.accidentCar.displacement === "string") {
            reqBody.accidentCar.displacement = Number(uncomma(reqBody.accidentCar.displacement));
        }

        return reqBody;
    };

    // Validation 체크 함수
    const validateRegister = () => {
        // 필수 필드 검증 함수
        const validateField = (condition, message) => {
            if (condition) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: message,
                }));
                return false;
            }
            return true;
        };

        // 문자열 필드 검증
        const validateStringField = (value, message) => {
            // value가 문자열이 아니면 빈 문자열로 변환
            const strValue = typeof value === "string" ? value : "";
            return validateField(strValue.trim() === "", message);
        };

        // 숫자 필드 검증
        const validateNumberField = (value, message) => {
            return validateField(value == null || value === "", message);
        };

        // 주민번호 검증
        const validateIdNumber = (idNumberF, idNumberB, prefix, isRequired = true) => {
            if (isRequired) {
                if (!validateStringField(idNumberF, `${prefix} 주민번호 앞 6자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberF.trim().length !== 6, `${prefix} 주민번호 앞자리는 6자리여야 합니다.`)) return false;
                if (!validateStringField(idNumberB, `${prefix} 주민번호 뒤 7자리를 입력해주세요.`)) return false;
                if (!validateField(idNumberB.trim().length !== 7, `${prefix} 주민번호 뒷자리는 7자리여야 합니다.`)) return false;
            }
            return true;
        };

        // 주소 검증
        const validateAddress = (address, addressDetail, prefix) => {
            if (!validateStringField(address, `${prefix} 주소를 입력해주세요.`)) return false;
            if (!validateStringField(addressDetail, `${prefix} 상세주소를 입력해주세요.`)) return false;
            return true;
        };

        // 운전자 정보 검증
        const validateDriver = (driver, prefix) => {
            if (!driver) return validateField(true, `${prefix} 정보를 입력해주세요.`);
            if (!validateStringField(driver.name, `${prefix}명을 입력해주세요.`)) return false;
            if (!validateStringField(driver.phone, `${prefix} 연락처를 입력해주세요.`)) return false;
            if (!validateIdNumber(driver.idNumberF, driver.idNumberB, prefix)) return false;
            if (!validateAddress(driver.address, driver.addressDetail, prefix)) return false;
            return true;
        };

        // 3. 계약 유형 검증
        if (!validateStringField(formData.contractType, "계약 유형을 선택해주세요.")) return false;

        // 5. 사고차량 정보 검증
        if (!formData.accidentCar) return validateField(true, "사고차량 정보를 입력해주세요.");
        if (!validateStringField(formData.accidentCar.carNumber, "사고차량 번호를 입력해주세요.")) return false;
        if (!validateStringField(formData.accidentCar.bmNo, "사고차량 제조사를 입력해주세요.")) return false;
        if (!validateStringField(formData.accidentCar.boiNo, "사고차량 모델을 입력해주세요.")) return false;

        // 6. 계약 정보 및 계약차량 검증
        if (!formData.contracts || formData.contracts.length === 0) {
            return validateField(true, "계약 정보가 없습니다.");
        }

        for (const contract of formData.contracts) {
            if (!contract.contractCar) return validateField(true, "계약차량 정보가 누락되었습니다.");
            if (!validateStringField(contract.contractCar.carNumber, "계약차량 번호를 입력해주세요.")) return false;
            if (!validateStringField(contract.contractCar.manufacturerName, "계약차량 제조사를 입력해주세요.")) return false;
            if (!validateStringField(contract.contractCar.modelName, "계약차량 모델을 입력해주세요.")) return false;
        }

        // 7. 보험사 정보 검증
        if (!formData.contractInsurance) return validateField(true, "보험사 정보를 입력해주세요.");
        if (!validateStringField(formData.contractInsurance.insuranceCorpName, "보험사명을 입력해주세요.")) return false;
        // if (!validateStringField(formData.contractInsurance.receiptNumber, "접수번호를 입력해주세요.")) return false;
        // if (!validateStringField(formData.contractInsurance.chargeMethod, "청구방법을 선택해주세요.")) return false;
        // if (!validateNumberField(formData.contractInsurance.chargeRate, "청구율을 선택해주세요.")) return false;

        // 12. 사업자 정보 검증 (사업자 정보가 있는 경우)
        if (formData.businessStatus) {
            if (!formData.contractBusinessInfo) return validateField(true, "사업자 정보를 입력해주세요.");
            if (!validateStringField(formData.contractBusinessInfo.corpName, "사업자 이름을 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.bizNumber, "사업자 번호를 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.managerName, "담당자 이름을 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.managerPhone, "담당자 연락처를 입력해주세요.")) return false;
            if (!validateStringField(formData.contractBusinessInfo.address, "사업자 주소를 입력해주세요.")) return false;

            const hasBizLic = formData.contractUploads?.some((item) => item.uploadType === "BIZ_LIC" && item.upload);
            if (!hasBizLic) return validateField(true, "사업자등록증 첨부파일을 첨부해주세요.");
        }

        return true;
    };

    const handleSubmit = async (action) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = buildRequestBody();
            await action(requestBody);

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/contract/status"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
                isLoading: false,
            }));
        } finally {
        }
    };

    // 차량 등록 시 호출 (CREATE)
    const handleSaveConfirm = async () => {
        await handleSubmit((requestBody) => postContractGroup(requestBody).unwrap());
    };

    // 차량 수정 시 호출 (UPDATE)
    const handleUpdateConfirm = async () => {
        await handleSubmit((requestBody) => putContractGroup({ contractGroupId: formData.id, body: requestBody }).unwrap());
    };

    const handleSave = () => {
        if (!validateRegister()) return;

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `계약서 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: mode === "CREATE" ? handleSaveConfirm : handleUpdateConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (postData) {
            navigate(`/contract/edit/${postData?.data.id}`);
        }
    }, [postData]);

    useEffect(() => {
        if (putData) {
            window.location.reload();
        }
    }, [putData]);

    const showConfirm = (title, description, buttons) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: title,
                confirmDescription: description,
                confirmButtons: buttons,
            },
        }));
    };

    // 삭제 확인 핸들러 (API 호출)
    const handleDeleteConfirm = async () => {
        try {
            await deleteContractGroup({
                contractGroupId: formData.id,
            }).unwrap();
            showConfirm("삭제가 완료되었습니다.", "", [
                {
                    label: "확인",
                    className: "btn-primary",
                    onClick: () => navigate("/contract/status"),
                },
            ]);
        } catch (error) {
            console.error("Failed to delete contract:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "계약서 정보 삭제에 실패했습니다.",
            }));
        }
    };

    // 삭제 버튼 클릭 시 확인 모달을 띄우고, "예" 버튼 클릭 시 handleDeleteConfirm 실행
    const handleDelete = () => {
        showConfirm("해당 계약서 정보를 삭제하시겠습니까?", "삭제 후에는 복구할 수 없습니다.", [
            { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
            {
                label: "아니오",
                className: "btn-cancel",
                onClick: () =>
                    setFormData((prev) => ({
                        ...prev,
                        confirmStatus: { confirmPopupStatus: false },
                    })),
            },
        ]);
    };

    // 공통 파일 업로드 핸들러
    const handleCommonFileChange = async (e, uploadType, idx) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;
            setFormData((prev) => {
                // 만약 uploadType이 "ADD_TERM"이면, contractTerms[idx] 업데이트
                if (uploadType === "ADD_TERM") {
                    const updatedTerms = [...(prev.contractTerms || [])];
                    updatedTerms[idx] = {
                        ...updatedTerms[idx],
                        upload: { id: newUploadId },
                        fileName: fileObj.name,
                    };
                    return { ...prev, contractTerms: updatedTerms };
                }
                // 그 외: contractUploads 배열 업데이트
                const updatedUploads = [...(prev.contractUploads || [])];
                const index = updatedUploads.findIndex((x) => x.uploadType === uploadType);
                if (index >= 0) {
                    updatedUploads[index] = {
                        ...updatedUploads[index],
                        upload: { id: newUploadId, fileName: fileObj.name },
                    };
                } else {
                    updatedUploads.push({ uploadType, upload: { id: newUploadId, fileName: fileObj.name } });
                }
                return { ...prev, contractUploads: updatedUploads };
            });
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "파일 업로드 실패",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const getFileName = (uploadType) => {
        const uploadObj = formData.contractUploads?.find((x) => x.uploadType === uploadType);
        return uploadObj?.upload?.fileName || "";
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">
                                {mode === "CREATE" ? "기본 정보 등록" : "기본 정보 수정"}{" "}
                                {formData.orderId && <span className="call-contract">CALL</span>}
                            </span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options"></div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 유형</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ContractInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head" style={{ display: "flex", justifyContent: "flex-start", gap: "12px" }}>
                                    <span className="form-item_tit">사업자 정보</span>
                                    <div className="form-item_etc">
                                        <div className="checkbox">
                                            <input
                                                type="checkbox"
                                                id="businessStatus"
                                                name="businessStatus"
                                                checked={formData.businessStatus}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        businessStatus: e.target.checked,
                                                        contractBusinessInfo: e.target.checked ? prev.contractBusinessInfo : null,
                                                    }))
                                                }
                                            />
                                            <label htmlFor="businessStatus">사업자가 있습니다.</label>
                                        </div>
                                    </div>
                                </div>
                                {formData.businessStatus && (
                                    <div className="item-body">
                                        <div className="item-list">
                                            <BusinessInfo
                                                formData={formData}
                                                handleChange={handleChange}
                                                setFormData={setFormData}
                                                getFileName={getFileName}
                                                handleCommonFileChange={handleCommonFileChange}
                                                disabled={!formData.businessStatus}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사고차량 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        {/* AccidentInfo는 그룹 레벨의 accidentCar 사용 */}
                                        <AccidentInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험사 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            hideFields={["chargeMethod", "managerName", "managerPhone"]}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기타 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OtherInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 관련 서류</span>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <AttachedFile
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            getFileName={getFileName}
                                            handleCommonFileChange={handleCommonFileChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* {(mode !== "CREATE" || formData.orderId) && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">종합 청구 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <TotalBillingInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        </div>
                                    </div>
                                    {mode !== "CREATE" && (
                                        <div className="item-bottom">
                                            <div className="bottom-option">
                                                <button type="button" className="btn btn-xs btn-primary" onClick={handleCharge}>
                                                    청구하기
                                                </button>
                                                <button type="button" className="btn btn-xs btn-secondary" onClick={handlePreCharge}>
                                                    선청구 하기
                                                </button>
                                                {formData.faxFileUrl ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-secondary"
                                                        onClick={() => handleShowFaxFile(formData.faxFileUrl || "")}
                                                    >
                                                        청구 파일 미리보기
                                                    </button>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )} */}
                        </div>
                    </div>

                    <div className="form-button">
                        <div className="button-row">
                            <button type="button" className="btn btn-xs btn-outline" onClick={() => navigate(-1)}>
                                뒤로가기
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={handleSave}>
                                계약서 저장
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => {
                            // BUSINESS(사업자) 주소 처리
                            if (prev.addressPopupTarget === "BUSINESS") {
                                return {
                                    ...prev,
                                    contractBusinessInfo: {
                                        ...prev.contractBusinessInfo,
                                        address: data.address,
                                        addressDetail: data.addressDetail,
                                        bCode: data.bCode,
                                    },
                                    addressPopupStatus: false,
                                    addressPopupTarget: null,
                                };
                            }

                            // 운전자 주소 처리
                            const [driverType, contractIndex] = prev.addressPopupTarget.split("_");
                            const updatedContracts = [...prev.contracts];
                            const contract = updatedContracts[contractIndex];

                            // contractDrivers가 없으면 빈 배열로 초기화
                            const updatedDrivers = (contract.contractDrivers || []).map((driver) => {
                                if (driver.contractDriverType === driverType) {
                                    return {
                                        ...driver,
                                        address: data.address,
                                        addressDetail: data.addressDetail,
                                        bCode: data.bCode,
                                    };
                                }
                                return driver;
                            });

                            updatedContracts[contractIndex] = {
                                ...contract,
                                contractDrivers: updatedDrivers,
                            };

                            return {
                                ...prev,
                                contracts: updatedContracts,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                        })
                    }
                />
            )}
        </>
    );
};
